import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import Layout from './components/Layout';
import LoginPage from './pages/Login';
import Dashboard from './pages/Dashboard';
import LeavesPage from './pages/Leaves';
import HoursPage from './pages/Hours';
import { SuppliesPage, EmployeesPage, DocumentsPage, ReportsPage, SettingsPage, AuditPage, AccountPage, LeaveBalancesPage } from './pages/Pages';
import EventsPage from './pages/Events';
import AvailabilityPage from './pages/Availability';
import OnboardingPage from './pages/Onboarding';
import SetPasswordPage from './pages/SetPassword';
import MessagesPage from './pages/Messages';
import MyDocumentsPage from './pages/MyDocuments';
import SettlementsPage from './pages/Settlements';
import StaffPage from './pages/Staff';
import StructurePage from './pages/Structure';
import MyTeamPage from './pages/MyTeam';
import './styles/global.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading, needsOnboarding, needsPasswordChange } = useAuth();
  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#94A3B8' }}>Ładowanie...</div>;
  if (!user) return <Navigate to="/login" />;
  // T172: hasło startowe od admina — najpierw własne hasło (serwer i tak nie wpuści dalej)
  if (needsPasswordChange) return <Navigate to="/set-password" />;
  // T47: eventowy bez uzupełnionych danych trafia na formularz i nigdzie indziej
  if (needsOnboarding) return <Navigate to="/onboarding" />;
  return children;
};

const SetPasswordRoute = ({ children }) => {
  const { user, loading, needsPasswordChange } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" />;
  return needsPasswordChange ? children : <Navigate to="/" />;
};

const OnboardingRoute = ({ children }) => {
  const { user, loading, needsOnboarding, needsPasswordChange } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" />;
  if (needsPasswordChange) return <Navigate to="/set-password" />;
  return needsOnboarding ? children : <Navigate to="/" />;
};

// Kto rozlicza urlopy (pole canSettleLeaves) i Admin — do sald urlopowych (T26)
const OfficeRoute = ({ children }) => {
  const { canSettleLeaves, canAccounting } = useAuth();
  return canSettleLeaves || canAccounting ? children : <Navigate to="/" />;
};

// T113: „Kadry” — uprawnienie „Kadry i księgowość” i admin
const AccountingRoute = ({ children }) => {
  const { canAccounting } = useAuth();
  return canAccounting ? children : <Navigate to="/" />;
};

// T87: ekran rozliczeń — admin i „Rozlicza wypłaty"
const PayrollRoute = ({ children }) => {
  const { canSettlePayroll } = useAuth();
  return canSettlePayroll ? children : <Navigate to="/" />;
};

const AdminRoute = ({ children }) => {
  const { isAdmin } = useAuth();
  return isAdmin ? children : <Navigate to="/" />;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/set-password" element={<SetPasswordRoute><SetPasswordPage /></SetPasswordRoute>} />
      <Route path="/onboarding" element={<OnboardingRoute><OnboardingPage /></OnboardingRoute>} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="leaves" element={<LeavesPage />} />
        <Route path="leave-balances" element={<OfficeRoute><LeaveBalancesPage /></OfficeRoute>} />
        <Route path="hours" element={<HoursPage key="hours" />} />
        <Route path="expenses" element={<HoursPage key="expenses" expensesOnly />} /> {/* T151 */}
        <Route path="events" element={<EventsPage />} />
        <Route path="availability" element={<AvailabilityPage />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="my-documents" element={<MyDocumentsPage />} />
        <Route path="settlements" element={<PayrollRoute><SettlementsPage /></PayrollRoute>} />
        <Route path="supplies" element={<SuppliesPage />} />
        <Route path="account" element={<AccountPage />} />
        <Route path="employees" element={<AdminRoute><EmployeesPage /></AdminRoute>} />
        <Route path="staff" element={<AccountingRoute><StaffPage /></AccountingRoute>} />
        <Route path="structure" element={<AdminRoute><StructurePage /></AdminRoute>} />
        <Route path="team" element={<MyTeamPage />} />
        <Route path="documents" element={<AdminRoute><DocumentsPage /></AdminRoute>} />
        <Route path="reports" element={<AdminRoute><ReportsPage /></AdminRoute>} />
        <Route path="audit" element={<AdminRoute><AuditPage /></AdminRoute>} />
        <Route path="settings" element={<AdminRoute><SettingsPage /></AdminRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
